import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const generateMathProblem = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  const operators = ['+', '-', '*'];
  const operator = operators[Math.floor(Math.random() * operators.length)];
  
  let answer;
  let display;
  
  switch (operator) {
    case '+':
      answer = num1 + num2;
      display = `${num1} + ${num2}`;
      break;
    case '-':
      // Ensure positive result
      const [bigger, smaller] = num1 > num2 ? [num1, num2] : [num2, num1];
      answer = bigger - smaller;
      display = `${bigger} - ${smaller}`;
      break;
    case '*':
      // Use smaller numbers for multiplication
      const a = Math.floor(Math.random() * 5) + 1;
      const b = Math.floor(Math.random() * 5) + 1;
      answer = a * b;
      display = `${a} × ${b}`;
      break;
    default:
      answer = num1 + num2;
      display = `${num1} + ${num2}`;
  }
  
  return { display, answer };
};

export const DeleteConfirmDialog = ({ 
  open, 
  onOpenChange, 
  itemType, 
  itemName,
  onConfirm,
  isDeleting = false
}) => {
  const [mathProblem, setMathProblem] = useState({ display: '', answer: 0 });
  const [userAnswer, setUserAnswer] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setMathProblem(generateMathProblem());
      setUserAnswer('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    const parsedAnswer = parseInt(userAnswer, 10);
    
    if (isNaN(parsedAnswer)) {
      setError('Please enter a number');
      return;
    }
    
    if (parsedAnswer !== mathProblem.answer) {
      setError('Incorrect answer. Please try again.');
      setMathProblem(generateMathProblem());
      setUserAnswer('');
      return;
    }
    
    onConfirm();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Confirm Deletion
          </DialogTitle>
          <DialogDescription className="pt-2">
            You are about to permanently delete this <strong>{itemType}</strong>
            {itemName && (
              <>: <strong className="text-foreground">"{itemName}"</strong></>
            )}
            . This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-muted/50 border rounded-lg p-4">
            <Label className="text-sm font-medium">
              To confirm deletion, solve this math problem:
            </Label>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xl font-mono font-bold bg-background px-3 py-2 rounded border">
                {mathProblem.display} = ?
              </span>
              <Input
                type="number"
                value={userAnswer}
                onChange={(e) => {
                  setUserAnswer(e.target.value);
                  setError('');
                }}
                onKeyDown={handleKeyDown}
                placeholder="Answer"
                className="w-24 text-center text-lg font-mono"
                autoFocus
                data-testid="captcha-input"
              />
            </div>
            {error && (
              <p className="text-destructive text-sm mt-2">{error}</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            data-testid="delete-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting || !userAnswer}
            data-testid="delete-confirm-btn"
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
